import React from 'react';
import lodash from 'lodash';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClock, faHourglass, faSun} from '@fortawesome/free-regular-svg-icons';
import {faList, faTemperature3} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import {Badge, UncontrolledTooltip} from 'reactstrap';

import {REALTIME_COUNTDOWN, REALTIME_COUNTUP, REALTIME_CURRENT_TIME, REALTIME_CURRENT_WEATHER, REALTIME_FORECAST_TYPES} from '../../../../../constants/realtimeFeedConstants';

const REALTIME_TYPE_DATA_MAP = {
  [REALTIME_CURRENT_TIME]: {
    id: [REALTIME_CURRENT_TIME],
    label: 'Time',
    tooltip: 'This content uses realtime time data that will be displayed over this file on your sign. Placeholders are shown in the preview.',
    icon: faClock,
  },
  [REALTIME_CURRENT_WEATHER]: {
    id: [REALTIME_CURRENT_WEATHER],
    label: 'Temp',
    tooltip: 'This content uses realtime weather data that will be displayed over this file on your sign. Placeholders are shown in the preview.',
    icon: faTemperature3,
  },
  forecast: {
    id: 'forecast',
    label: 'Forecast',
    tooltip: 'This content uses realtime forecast data that will be displayed over this file on your sign. Placeholders are shown in the preview.',
    icon: faSun,
  },
  countdown: {
    id: 'countdown',
    label: 'Countdown',
    tooltip: 'This content uses realtime date data that will display a countdown on your sign. Placeholders are shown in the preview.',
    icon: faHourglass,
  },
  countup: {
    id: 'countup',
    label: 'Countup',
    tooltip: 'This content uses realtime date data that will display a countup on your sign. Placeholders are shown in the preview.',
    icon: faHourglass,
  },
  sheet: {
    id: 'sheet',
    label: 'Sheet',
    tooltip: 'This content uses sheet data that will display over this file on your sign. Placeholders are shown in the preview.',
    icon: faList,
  },
};

/**
 * The ContentBadges component.
 */
export class ContentBadges extends React.Component {
  /**
   * Gets the list id and type from the params.
   *
   * @param {{}} content
   * @param {{}} user
   * @returns {boolean}
   */
  getContentBadges = (content, user) => {
    if (!content || !content.sources) {
      return [];
    }

    const allFeedContentEntities = lodash
      .flattenDeep(Object.values(content.sources).map((source) => source.entities))
      .filter((contentEntity) => contentEntity.type === 'feed');

    const uniqueRealtimeDataTypes = {};
    allFeedContentEntities.forEach((feedContentEntities) => {
      const realtimeType = feedContentEntities.feed && feedContentEntities.feed.type;

      if (REALTIME_TYPE_DATA_MAP[realtimeType]) {
        uniqueRealtimeDataTypes[realtimeType] = REALTIME_TYPE_DATA_MAP[realtimeType];
      } else if (REALTIME_FORECAST_TYPES.includes(realtimeType)) {
        uniqueRealtimeDataTypes.forecast = REALTIME_TYPE_DATA_MAP.forecast;
      } else if (new RegExp(realtimeType).test(REALTIME_COUNTDOWN)) {
        uniqueRealtimeDataTypes.countdown = REALTIME_TYPE_DATA_MAP.countdown;
      } else if (new RegExp(realtimeType).test(REALTIME_COUNTUP)) {
        uniqueRealtimeDataTypes.countup = REALTIME_TYPE_DATA_MAP.countup;
      } else if (realtimeType.includes('sheet')) {
        uniqueRealtimeDataTypes.sheet = REALTIME_TYPE_DATA_MAP.sheet;
      }
    }, {});

    if (content.hasExtendedWeather && user && user.plan && !user.plan.hasPremiumAccess) {
      uniqueRealtimeDataTypes.upgrade = {
        id: 'upgrade',
        label: 'Upgrade Plan',
        tooltip: 'This file uses premium features. Please upgrade your Project Content plan to enjoy dynamic forecasts.',
        color: 'warning'
      };
    } else if (content.hasPaidSheet && user && user.plan && !user.plan.hasEssentialAccess) {
      uniqueRealtimeDataTypes.upgrade = {
        id: 'upgrade',
        label: 'Upgrade Plan',
        tooltip: 'This file uses a paid sheet. Please upgrade your Project Content plan to enjoy dynamic content.',
        color: 'warning'
      };
    } else if (content.isInvalid && user) {
      uniqueRealtimeDataTypes.upgrade = {
        id: 'upgrade',
        label: 'Upgrade Plan',
        tooltip: 'This file uses premium features. Please upgrade your Project Content plan to enjoy dynamic content.',
        color: 'warning'
      };
    }

    return Object.values(uniqueRealtimeDataTypes);
  };

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {content, user} = this.props;

    const contentBadges = this.getContentBadges(content, user);

    return (
      <div className="">
        {contentBadges.map((contentBadge) => (
          <React.Fragment key={contentBadge.id}>
            <Badge className="mr-2 text-light" color={contentBadge.color || 'info'} id={`content-realtime-badge-${contentBadge.id}`}>
              <FontAwesomeIcon
                icon={contentBadge.icon}
                className="mr-2"
              />
              {contentBadge.label}
            </Badge>
            <UncontrolledTooltip
              placement="top"
              target={`content-realtime-badge-${contentBadge.id}`}
            >
              {contentBadge.tooltip}
            </UncontrolledTooltip>
          </React.Fragment>
        ))}
      </div>
    );
  }
}

ContentBadges.propTypes = {
  content: PropTypes.object.isRequired,
  user: PropTypes.object,
};

export default ContentBadges;
